import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { FiInbox, FiSettings } from "react-icons/fi";
import { useSavedOrgPreferences } from "../../../hooks/useSavedOrgPreferences";
import { useSavePreferences } from "../api/useSavePreferences";

const PreferencesModal = ({ isOpen, onClose }) => {
  // Performance optimization: only fetch when modal is open
  const { savedPreferences, hasPreferences, isLoading } = useSavedOrgPreferences(isOpen);
  const [selectedOrgs, setSelectedOrgs] = useState({});
  const [bulletConfig, setBulletConfig] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Use the preferences mutation hook
  const { mutate: savePreferences, isPending: isSaving } = useSavePreferences();

  // Initialize state whenever modal opens or preferences load
  useEffect(() => {
    if (!isOpen || isLoading || savedPreferences.length === 0) return;
    const initialSelected = {};
    const initialConfig = {};
    savedPreferences.forEach((pref) => {
      initialSelected[pref.workHistoryId] = true;
      // Clamp to maxBulletsAllowed so we never start with an invalid value
      const max = pref.maxBulletsAllowed ?? 10;
      initialConfig[pref.workHistoryId] = Math.min(pref.bulletCount, max);
    });
    setSelectedOrgs(initialSelected);
    setBulletConfig(initialConfig);
  }, [isOpen, isLoading, savedPreferences]);

  const handleCheckboxChange = (workHistoryId) => {
    setSelectedOrgs((prev) => ({
      ...prev,
      [workHistoryId]: !prev[workHistoryId],
    }));
    setHasChanges(true);
  };

  const handleBulletChange = (workHistoryId, value, maxAllowed) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 1 && numValue <= (maxAllowed ?? 10)) {
      setBulletConfig((prev) => ({
        ...prev,
        [workHistoryId]: numValue,
      }));
      setHasChanges(true);
    }
  };

  const handleIncrement = (workHistoryId, maxAllowed) => {
    const currentValue = bulletConfig[workHistoryId] || 3;
    if (currentValue < (maxAllowed ?? 10)) {
      setBulletConfig((prev) => ({
        ...prev,
        [workHistoryId]: currentValue + 1,
      }));
      setHasChanges(true);
    }
  };

  const handleDecrement = (workHistoryId) => {
    const currentValue = bulletConfig[workHistoryId] || 3;
    if (currentValue > 1) {
      setBulletConfig((prev) => ({
        ...prev,
        [workHistoryId]: currentValue - 1,
      }));
      setHasChanges(true);
    }
  };

  const handleSave = () => {
    // Build preferences array for batch update
    // Include all orgs that have a bullet config entry
    const preferences = Object.entries(bulletConfig)
      .map(([workHistoryId, preferredBulletCount]) => ({
        workHistoryId,
        preferredBulletCount,
      }));

    savePreferences(
      { preferences },
      {
        onSuccess: () => {
          setHasChanges(false);
          onClose();
        },
      }
    );
  };

  const handleCancel = () => {
    // Reset to original values
    const initialSelected = {};
    const initialConfig = {};
    savedPreferences.forEach((pref) => {
      initialSelected[pref.workHistoryId] = true;
      initialConfig[pref.workHistoryId] = pref.bulletCount;
    });
    setSelectedOrgs(initialSelected);
    setBulletConfig(initialConfig);
    setHasChanges(false);
    onClose();
  };

  const selectedCount = Object.values(selectedOrgs).filter(Boolean).length;
  const totalCount = savedPreferences.length;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleCancel} 
      size="lg" 
      isCentered
      scrollBehavior="inside"
      blockScrollOnMount={false}
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(4px)" />
      <ModalContent mx={4} borderRadius="2xl" boxShadow="xl">
        <ModalHeader borderBottomWidth="1px" borderColor="gray.100" py={4} px={6}>
          <HStack spacing={2}>
            <Box w="3px" h="20px" bg="purple.500" borderRadius="full" />
            <Text fontSize="lg" fontWeight="600" color="gray.800">
              Preferences
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={4} right={4} _focus={{ boxShadow: "none" }} />

        <ModalBody py={5} px={6}>
          {isLoading ? (
            <Box py={8} textAlign="center">
              <Text fontSize="sm" color="gray.500">
                Loading preferences...
              </Text>
            </Box>
          ) : !hasPreferences ? (
            // Empty state
            <VStack spacing={3} py={8} textAlign="center">
              <Icon as={FiInbox} boxSize={12} color="gray.300" />
              <Text fontSize="md" fontWeight="500" color="gray.600">
                No preferences yet
              </Text>
              <Text fontSize="sm" color="gray.500" maxW="280px">
                Create your first application to set organization bullet preferences
              </Text>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={5}>
              {/* Header with icon */}
              <Box bg="purple.50" p={4} borderRadius="xl" border="1px solid" borderColor="purple.100">
                <HStack spacing={3}>
                  <Icon as={FiSettings} color="purple.600" boxSize={5} />
                  <VStack align="start" spacing={0.5}>
                    <Text fontSize="sm" fontWeight="600" color="purple.900">
                      Customize your resume output
                    </Text>
                    <Text fontSize="xs" color="purple.700">
                      Select organizations and set max bullets for each experience
                    </Text>
                  </VStack>
                </HStack>
              </Box>

              {/* Org list with scrollbar */}
              <VStack
                align="stretch"
                spacing={3}
                maxH="340px"
                overflowY="auto"
                pr={1}
                css={{
                  "&::-webkit-scrollbar": {
                    width: "5px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#CBD5E0",
                    borderRadius: "24px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#A0AEC0",
                  },
                }}
              >
                {savedPreferences.map((pref) => {
                  const isSelected = selectedOrgs[pref.workHistoryId];
                  const bulletCount = bulletConfig[pref.workHistoryId] ?? pref.bulletCount;

                  return (
                    <Box
                      key={pref.workHistoryId}
                      p={4}
                      bg={isSelected ? "white" : "gray.50"}
                      border="1px solid"
                      borderColor={isSelected ? "gray.200" : "gray.100"}
                      borderRadius="xl"
                      transition="all 0.15s ease"
                      _hover={{
                        borderColor: isSelected ? "purple.200" : "gray.200",
                        boxShadow: isSelected ? "sm" : "none",
                      }}
                    >
                      <HStack justify="space-between" align="center" spacing={4}>
                        {/* Checkbox + Org Name + Role */}
                        {/* <Checkbox
                          isChecked={isSelected}
                          onChange={() => handleCheckboxChange(pref.workHistoryId)}
                          colorScheme="purple"
                          size="lg"
                          _focus={{ boxShadow: "none" }}
                        > */}
                          <VStack align="start" spacing={0.5}>
                            <Text
                              fontSize="sm"
                              fontWeight="500"
                              color={isSelected ? "gray.800" : "gray.500"}
                              lineHeight="1.3"
                            >
                              {pref.organization}
                            </Text>
                            <Text
                              fontSize="xs"
                              color={isSelected ? "gray.500" : "gray.400"}
                              lineHeight="1.2"
                            >
                              {pref.role}
                            </Text>
                          </VStack>
                        {/* </Checkbox> */}

                        {/* Max bullets input */}
                        <HStack spacing={2}>
                          <Text fontSize="xs" color="gray.500" fontWeight="500">
                            Max bullets:
                          </Text>
                          <HStack spacing={0.5}>
                            <IconButton
                              icon={<Text fontSize="md" fontWeight="bold" color="gray.600">−</Text>}
                              size="xs"
                              variant="ghost"
                              onClick={() => handleDecrement(pref.workHistoryId)}
                              isDisabled={!isSelected || bulletCount <= 1}
                              _hover={{ bg: "gray.100" }}
                              _active={{ bg: "gray.200" }}
                              _focus={{ boxShadow: "none" }}
                              aria-label="Decrease bullets"
                              minW="24px"
                              h="24px"
                            />
                            <Input
                              value={bulletCount}
                              onChange={(e) => handleBulletChange(pref.workHistoryId, e.target.value, pref.maxBulletsAllowed)}
                              size="sm"
                              w="45px"
                              h="28px"
                              textAlign="center"
                              fontWeight="600"
                              fontSize="sm"
                              color="purple.600"
                              bg={isSelected ? "white" : "gray.100"}
                              border="1px solid"
                              borderColor={isSelected ? "gray.200" : "gray.100"}
                              isDisabled={!isSelected}
                              _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
                              _focus={{ borderColor: "purple.300", boxShadow: "none" }}
                              borderRadius="md"
                            />
                            <IconButton
                              icon={<Text fontSize="md" fontWeight="bold" color="gray.600">+</Text>}
                              size="xs"
                              variant="ghost"
                              onClick={() => handleIncrement(pref.workHistoryId, pref.maxBulletsAllowed)}
                              isDisabled={!isSelected || bulletCount >= (pref.maxBulletsAllowed ?? 10)}
                              _hover={{ bg: "gray.100" }}
                              _active={{ bg: "gray.200" }}
                              _focus={{ boxShadow: "none" }}
                              aria-label="Increase bullets"
                              minW="24px"
                              h="24px"
                            />
                          </HStack>
                        </HStack>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>

              {/* Selection count */}
              <Text fontSize="xs" color="gray.500" textAlign="center" fontWeight="500">
                {selectedCount} of {totalCount} organization{totalCount !== 1 ? 's' : ''} selected
              </Text>
            </VStack>
          )}
        </ModalBody>

        {hasPreferences && (
          <ModalFooter borderTopWidth="1px" borderColor="gray.100" py={4} px={6}>
            <HStack spacing={3} width="100%">
              <Button 
                variant="ghost" 
                onClick={handleCancel} 
                size="sm"
                fontSize="sm"
                flex={1}
                isDisabled={isSaving}
                _focus={{ boxShadow: "none" }}
                _hover={{ bg: "gray.100" }}
                borderRadius="full"
                fontWeight="500"
                color="gray.600"
              >
                Cancel
              </Button>
              <Button
                bgGradient="linear(to-r, purple.500, purple.600)"
                _hover={{
                  bgGradient: "linear(to-r, purple.600, purple.700)",
                  transform: "translateY(-1px)",
                  boxShadow: "md",
                }}
                _active={{
                  transform: "translateY(0)",
                }}
                _focus={{ boxShadow: "none" }}
                color="white"
                onClick={handleSave}
                isDisabled={Object.keys(bulletConfig).length === 0}
                isLoading={isSaving}
                loadingText="Saving..."
                size="sm"
                fontSize="sm"
                flex={1}
                borderRadius="full"
                fontWeight="500"
              >
                Apply Settings
              </Button>
            </HStack>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default React.memo(PreferencesModal);
